import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Search, Download, ArrowUpDown, ChevronLeft, ChevronRight, RefreshCw,
  Eye, Archive, RotateCcw, History as HistoryIcon, Megaphone,
} from 'lucide-react';
import { toast } from 'sonner';
import api, { formatErrorMessage } from '../utils/api';

/**
 * Tabla histórica de TODOS los prospectos (incluye archivados/ganados/perdidos).
 * Al hacer click en una fila se abre la ficha completa del lead (mismo modal del Kanban),
 * vía la prop `onLeadClick(id)` que provee el componente padre (Marketing.jsx).
 */

const ETAPA_COLORS = {
  'Por Contactar':       'bg-amber-100 text-amber-800 border-amber-300',
  'Contactado':          'bg-blue-100 text-blue-800 border-blue-300',
  'Sin Respuesta':       'bg-orange-100 text-orange-800 border-orange-300',
  'En Precalificación':  'bg-violet-100 text-violet-800 border-violet-300',
  'Reunión Socios':      'bg-indigo-100 text-indigo-800 border-indigo-300',
  'Aceptado':            'bg-emerald-100 text-emerald-800 border-emerald-300',
  'Recuperado':          'bg-teal-100 text-teal-800 border-teal-300',
  'Descartado':          'bg-slate-200 text-slate-700 border-slate-300',
  'Perdido':             'bg-rose-100 text-rose-800 border-rose-300',
};

const fmtFecha = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('es-CL', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const ResumenCell = ({ texto }) => {
  const [expanded, setExpanded] = useState(false);
  if (!texto) return <span className='text-slate-400'>—</span>;
  const preview = texto.length > 70 ? texto.slice(0, 70) + '…' : texto;
  return (
    <button
      onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
      className='text-left text-xs text-slate-700 hover:text-slate-900 max-w-[260px] block'
      title={expanded ? 'Colapsar' : 'Ver completo'}
    >
      {expanded ? texto : preview}
    </button>
  );
};

const HistorialLeads = ({ onLeadClick }) => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [estado, setEstado] = useState('todos');
  const [etapas, setEtapas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => { setPage(1); }, [debouncedSearch, estado]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
      if (debouncedSearch) params.set('search', debouncedSearch);
      if (estado && estado !== 'todos') params.set('estado', estado);
      const r = await api.get(`/leads/historial?${params.toString()}`);
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
      setPages(r.data.pages || 1);
      setEtapas(r.data.etapas || []);
    } catch (e) {
      toast.error(formatErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, debouncedSearch, estado]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const exportCSV = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (debouncedSearch) params.set('search', debouncedSearch);
      if (estado && estado !== 'todos') params.set('estado', estado);
      const r = await api.get(`/leads/historial/export.csv?${params.toString()}`,
        { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `historial_leads_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Histórico exportado');
    } catch (e) {
      toast.error(formatErrorMessage(e));
    } finally {
      setExporting(false);
    }
  };

  const handleReactivar = async (e, lead) => {
    e.stopPropagation();
    try {
      await api.post(`/leads/${lead.id}/reactivar`);
      toast.success(`${lead.nombre || 'Lead'} reactivado en "Por Contactar"`);
      fetchData();
    } catch (e2) {
      toast.error(formatErrorMessage(e2));
    }
  };

  const handleArchivar = async (e, lead) => {
    e.stopPropagation();
    try {
      await api.post(`/leads/${lead.id}/archivar`);
      toast.success(`${lead.nombre || 'Lead'} archivado`);
      fetchData();
    } catch (e2) {
      toast.error(formatErrorMessage(e2));
    }
  };

  const labelRango = useMemo(() => {
    if (total === 0) return '0 registros';
    const desde = (page - 1) * pageSize + 1;
    const hasta = Math.min(page * pageSize, total);
    return `${desde}–${hasta} de ${total.toLocaleString('es-CL')}`;
  }, [page, pageSize, total]);

  return (
    <div className='space-y-4' data-testid='historial-leads'>
      {/* HEADER */}
      <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3'>
        <div>
          <h2 className='text-xl font-semibold text-slate-900 flex items-center gap-2'>
            <HistoryIcon className='w-5 h-5 text-indigo-600' />
            Historial de Leads
          </h2>
          <p className='text-sm text-slate-500'>
            Base inmutable de todos los prospectos recibidos · {total.toLocaleString('es-CL')} registros totales
          </p>
        </div>
        <Button onClick={exportCSV} disabled={exporting || loading}
                className='bg-emerald-600 hover:bg-emerald-700'
                data-testid='historial-export-btn'>
          <Download className='w-4 h-4 mr-2' />
          {exporting ? 'Exportando…' : 'Exportar Histórico'}
        </Button>
      </div>

      {/* FILTROS */}
      <Card className='p-3 border-slate-200'>
        <div className='flex flex-col sm:flex-row gap-3'>
          <div className='relative flex-1'>
            <Search className='w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2' />
            <Input
              type='text'
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder='Buscar por nombre, empresa, email, teléfono o campaña…'
              className='pl-9 h-10'
              data-testid='historial-search-input'
            />
          </div>
          <Select value={estado} onValueChange={setEstado}>
            <SelectTrigger className='w-full sm:w-56 h-10' data-testid='historial-filtro-estado'>
              <SelectValue placeholder='Filtrar por estado' />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value='todos'>Todos los estados</SelectItem>
              {etapas.map(et => (
                <SelectItem key={et} value={et}>{et}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant='outline' onClick={fetchData} disabled={loading}
                  className='h-10' title='Recargar'>
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </Card>

      {/* TABLA */}
      <Card className='border-slate-200 overflow-hidden'>
        <div className='overflow-x-auto'>
          <table className='w-full text-sm' data-testid='historial-table'>
            <thead className='bg-slate-50 border-b border-slate-200'>
              <tr>
                <th className='text-left px-3 py-2 font-semibold text-slate-700 w-32'>
                  <span className='inline-flex items-center gap-1'>Fecha <ArrowUpDown className='w-3 h-3 text-slate-400' /></span>
                </th>
                <th className='text-left px-3 py-2 font-semibold text-slate-700'>Nombre Completo</th>
                <th className='text-left px-3 py-2 font-semibold text-slate-700'>Empresa / Razón Social</th>
                <th className='text-left px-3 py-2 font-semibold text-slate-700 w-36'>Teléfono</th>
                <th className='text-left px-3 py-2 font-semibold text-slate-700'>Correo Electrónico</th>
                <th className='text-left px-3 py-2 font-semibold text-slate-700 w-40'>Campaña</th>
                <th className='text-left px-3 py-2 font-semibold text-slate-700 w-44'>Último Estado</th>
                <th className='text-left px-3 py-2 font-semibold text-slate-700'>Resumen Problema</th>
                <th className='text-right px-3 py-2 font-semibold text-slate-700 w-40'>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading && items.length === 0 ? (
                <tr><td colSpan={9} className='text-center py-12 text-slate-500'>
                  <RefreshCw className='w-5 h-5 animate-spin mx-auto mb-2 text-slate-400' />
                  Cargando registros…
                </td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={9} className='text-center py-12 text-slate-500'>
                  No se encontraron registros con los filtros aplicados.
                </td></tr>
              ) : items.map((lead) => {
                const nombre = lead.nombre || lead.name || '(Sin nombre)';
                const empresa = lead.empresa || lead.razon_social || lead.company || '—';
                const tel = lead.telefono || lead.phone || '—';
                const email = lead.email || '—';
                const etapa = lead.etapa || 'Por Contactar';
                const colorClass = ETAPA_COLORS[etapa] || 'bg-slate-100 text-slate-700 border-slate-300';
                const resumen = lead.resumen_problema || lead.descripcion_problema || lead.message || lead.problema || '';
                const campana = lead.campana_nombre || lead.campaign_name || lead.campaign || lead.utm_campaign;
                const origen = lead.origen || lead.source;
                const reactivable = lead.archivado || ['Descartado', 'Perdido'].includes(etapa);
                const archivable = !lead.archivado;
                return (
                  <tr
                    key={lead.id}
                    className='border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition'
                    onClick={() => onLeadClick && onLeadClick(lead.id)}
                    data-testid={`historial-row-${lead.id}`}
                  >
                    <td className='px-3 py-2 text-xs text-slate-500 font-mono whitespace-nowrap'>
                      {fmtFecha(lead.created_at)}
                    </td>
                    <td className='px-3 py-2 font-medium text-slate-900'>
                      {nombre}
                      {lead.archivado && (
                        <span className='ml-2 text-[10px] text-slate-500 inline-flex items-center gap-0.5'>
                          <Archive className='w-3 h-3' /> archivado
                        </span>
                      )}
                    </td>
                    <td className='px-3 py-2 text-slate-700'>{empresa}</td>
                    <td className='px-3 py-2 text-slate-700 font-mono text-xs'>{tel}</td>
                    <td className='px-3 py-2 text-slate-700 text-xs truncate max-w-[200px]' title={email}>{email}</td>
                    <td className='px-3 py-2'>
                      {campana ? (
                        <div className='inline-flex items-center gap-1 text-xs text-slate-700 max-w-[160px] truncate' title={campana}>
                          <Megaphone className='w-3 h-3 text-rose-500 shrink-0' />
                          <span className='truncate'>{campana}</span>
                        </div>
                      ) : origen ? (
                        <span className='text-xs text-slate-500'>{origen}</span>
                      ) : (
                        <span className='text-slate-400'>—</span>
                      )}
                    </td>
                    <td className='px-3 py-2'>
                      <Badge variant='outline' className={`text-[10px] ${colorClass}`}>{etapa}</Badge>
                    </td>
                    <td className='px-3 py-2'>
                      <ResumenCell texto={resumen} />
                    </td>
                    <td className='px-3 py-2 text-right whitespace-nowrap'>
                      <div className='inline-flex items-center gap-1'>
                        <Button
                          variant='ghost' size='sm'
                          onClick={(e) => { e.stopPropagation(); onLeadClick && onLeadClick(lead.id); }}
                          className='h-7 px-2 text-xs'
                          data-testid={`historial-ver-${lead.id}`}
                          title='Ver ficha completa'
                        >
                          <Eye className='w-3.5 h-3.5 mr-1' /> Ver
                        </Button>
                        {reactivable ? (
                          <Button
                            variant='outline' size='sm'
                            onClick={(e) => handleReactivar(e, lead)}
                            className='h-7 px-2 text-xs text-emerald-700 border-emerald-300 hover:bg-emerald-50'
                            data-testid={`historial-reactivar-${lead.id}`}
                            title='Reactivar en Pipeline'
                          >
                            <RotateCcw className='w-3.5 h-3.5' />
                          </Button>
                        ) : archivable ? (
                          <Button
                            variant='ghost' size='sm'
                            onClick={(e) => handleArchivar(e, lead)}
                            className='h-7 px-2 text-xs text-slate-500 hover:text-slate-700'
                            data-testid={`historial-archivar-${lead.id}`}
                            title='Archivar'
                          >
                            <Archive className='w-3.5 h-3.5' />
                          </Button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* PAGINACIÓN */}
        <div className='flex items-center justify-between px-3 py-2 border-t border-slate-200 bg-slate-50/50'>
          <p className='text-xs text-slate-600' data-testid='historial-rango'>{labelRango}</p>
          <div className='flex items-center gap-2'>
            <Button
              variant='outline' size='sm'
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page <= 1 || loading}
              className='h-7 px-2'
              data-testid='historial-prev-page'
            >
              <ChevronLeft className='w-3.5 h-3.5' />
            </Button>
            <span className='text-xs text-slate-700 font-medium px-2'>
              Página {page} de {pages}
            </span>
            <Button
              variant='outline' size='sm'
              onClick={() => setPage(p => Math.min(pages, p + 1))}
              disabled={page >= pages || loading}
              className='h-7 px-2'
              data-testid='historial-next-page'
            >
              <ChevronRight className='w-3.5 h-3.5' />
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default HistorialLeads;
